import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Map, AdvancedMarker, Pin, useMap } from '@vis.gl/react-google-maps';
import type { SiteRagReport } from '../../../types/siteRag';
import type { SiteEntity, SiteLayer } from '../../../types/site';
import type { ReportTheme } from '../../../types/reportTheme';
import { SiteLayerOverlays, SiteLayerItemSidebar } from '../SiteMapLayers';
import { MapCaption } from '../MapCaption';
import { EditorialFigureCaption } from './editorial/EditorialFigureCaption';
import { useViewMode } from '../viewMode';
import { HeroCoverBoundaryOverlay } from './covers/HeroCoverBoundaryOverlay';
import { SiteBoundaryParcelLegend } from './SiteBoundaryParcelLegend';
import { StaticMapImage } from './StaticMapImage';
import { resolveSiteMapFigure } from '../../../lib/siteMapTreatment';
import { roleColor } from '../../../lib/reportRoleColors';
import { useFigureNumber } from './figureCounter';
import { materializeSiteBoundaries, computeAggregate, isFiniteLatLng } from '../../../lib/siteBoundaryGeometry';
import { AURORA_PARCEL_COLORS } from '../../../config/siteLayerColors';

/** Per-parcel ring strokes for the static overview map — the SAME ordered
 *  palette the site panel's boundary map + per-parcel legend dots use
 *  (`AURORA_PARCEL_COLORS`), so the report figure colour-codes multi-parcel
 *  sites one-for-one with the app. Module-level for stable memo identity. */
const PARCEL_RING_COLORS = AURORA_PARCEL_COLORS.map((c) => c.stroke);

/** Maps key presence — when absent, the live `<Map>` cannot render tiles, so we
 *  fall back to the pure-SVG boundary diagram (never a blank frame). */
function hasGoogleMapsKey(): boolean {
  return Boolean(import.meta.env.VITE_GOOGLE_MAPS_API_KEY);
}

/**
 * Match the boundary overlay's reviewed acreage precision so its compact
 * visual label ("ac") has an exact, tagged text equivalent in the figure
 * caption ("acres").
 */
function formatSiteAreaAcres(siteArea: number | null | undefined): string | null {
  if (siteArea == null || !Number.isFinite(siteArea)) return null;
  const precision = siteArea >= 10 ? 1 : 2;
  const rounded = Number(siteArea.toFixed(precision));
  return `${rounded.toLocaleString('en-US', { maximumFractionDigits: precision })} acres`;
}

/** Renders a polygon overlay on the Google Map. */
const BoundaryPolygon: React.FC<{ path: { lat: number; lng: number }[] }> = ({ path }) => {
  const map = useMap();
  const polyRef = useRef<google.maps.Polygon | null>(null);

  useEffect(() => {
    if (!map || path.length < 3) return;
    const poly = new google.maps.Polygon({
      paths: path,
      strokeColor: '#D97706',
      strokeWeight: 2,
      fillColor: '#D97706',
      fillOpacity: 0.15,
      map,
    });
    polyRef.current = poly;
    return () => { poly.setMap(null); };
  }, [map, path]);

  return null;
};

export interface PropertyMapContentProps {
  report: SiteRagReport;
  reportSite?: SiteEntity;
  reportSiteLayers: SiteLayer[];
  surfaceMode: 'interactive' | 'static';
  onEditLayers?: () => void;
  /** Narrow column layout when embedded in overview section. @deprecated use layout */
  embedded?: boolean;
  /** Layout variant for map frame sizing. */
  layout?: 'default' | 'embedded' | 'keyFindings' | 'projectIntentOverview';
  /**
   * Phase 4 — report theme. Drives the per-theme site-map FIGURE treatment
   * (framed-figure / exhibit / full-bleed) and the chip colour tone. Absent ⇒
   * legacy plain framed map with the dark-map palette (byte-stable).
   */
  theme?: ReportTheme;
  /**
   * 1-based figure number for the numbered caption / exhibit header. When
   * provided it wins (explicit override). When omitted, the report-global
   * figure counter assigns the next sequential number off `figureKey` (so the
   * cover map, the overview map, and section figures don't all read "Figure 1").
   * Outside a FigureCounterProvider the counter is inert and this falls back to
   * 1 — byte-stable for standalone / test mounts.
   */
  figureNumber?: number;
  /**
   * Stable key for report-global figure numbering. Defaults to the layout
   * variant so the SAME map placement always recalls the SAME number across
   * re-renders. Pass a distinct key when two maps share a layout.
   */
  figureKey?: string;
  /**
   * RC4 — the un-merged theme-preset id (`presentation.presetId`). Authoritative
   * for the Monograph figure framing (supersedes the legacy heading-font sniff),
   * so a tenant font override can't silently revert the framing. Absent ⇒ the
   * resolver falls back to the historical font heuristic (byte-stable).
   */
  presetId?: string | null;
  /**
   * N1.5C-flat — editorial-monograph mode. When ON the map renders as an OPEN
   * FIGURE: no `rounded-*` corners, no fill, at most ONE `roleColor(theme,'hair')`
   * hairline, with a numbered `EditorialFigureCaption` below it (instead of the
   * legacy MapCaption). Absent/false keeps the legacy framed map byte-stable.
   */
  editorialActive?: boolean;
}

export const PropertyMapContent: React.FC<PropertyMapContentProps> = ({
  report,
  reportSite,
  reportSiteLayers,
  surfaceMode,
  embedded = false,
  layout,
  theme,
  figureNumber,
  figureKey,
  presetId,
  editorialActive = false,
}) => {
  const resolvedLayout = layout ?? (embedded ? 'embedded' : 'default');
  const isOverviewLayout = resolvedLayout === 'keyFindings' || resolvedLayout === 'projectIntentOverview';
  const isStatic = surfaceMode === 'static';
  const [hiddenLayerIds, setHiddenLayerIds] = useState<ReadonlySet<string>>(() => new Set());

  // Report-global sequential figure number. Explicit `figureNumber` wins; else
  // the counter assigns the next number off a stable key (defaults to the
  // layout variant). Outside a FigureCounterProvider this resolves to 1 (the
  // hook fallback) so standalone mounts stay byte-identical to the old default.
  const counterNumber = useFigureNumber(figureKey ?? `siteMap:${resolvedLayout}`);
  const resolvedFigureNumber = figureNumber ?? counterNumber;

  // Phase 4 — resolve the figure treatment. Absent theme/treatment ⇒ legacy
  // framed map (no numbered caption, no accent rule, dark-map tone).
  const figure = resolveSiteMapFigure(
    theme ?? { siteMapTreatment: undefined, figureNumberingPrefix: 'Figure ', headingFont: '' },
    resolvedFigureNumber,
    presetId,
  );

  const toggleLayer = useCallback((id: string) => {
    setHiddenLayerIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const acresStr = formatSiteAreaAcres(report.siteArea);
  const zoning = report.datasets?.parcel?.zoning;
  const zoningLabel = zoning ? `${String(zoning).toLowerCase()} zoned` : null;
  const coords = report.location
    && typeof report.location.lat === 'number' && Number.isFinite(report.location.lat)
    && typeof report.location.lng === 'number' && Number.isFinite(report.location.lng)
    ? `${report.location.lat.toFixed(4)}, ${report.location.lng.toFixed(4)}`
    : null;
  let layerSummary: string | null = null;
  if (reportSiteLayers.length > 0) {
    // The caption carries the layer COUNT only (never the per-layer name run-on
    // that duplicated the legend). J7 — the per-layer names now read off the
    // on-map boundary-style leader callouts; the caption stays concise.
    layerSummary = `${reportSiteLayers.length} layer${reportSiteLayers.length === 1 ? '' : 's'}`;
  }
  const primaryParts = [acresStr, zoningLabel, report.address].filter(Boolean) as string[];
  const secondaryParts = [coords, layerSummary].filter(Boolean) as string[];
  const path = reportSite?.siteBoundary ?? report.siteBoundary;
  const defaultMapTypeId = isOverviewLayout ? 'roadmap' : 'hybrid';

  // J7 — materialize the parcels + aggregate (prefer the live SiteEntity, fall
  // back to the report doc) so the overview map can drop the category-grouped
  // LAYERS legend (`SiteLayerSidebar`) and use the clean boundary-style
  // per-parcel ledger (`SiteBoundaryParcelLegend`) — the SAME legend the Site
  // Boundary visualization uses (TOTAL / dedup / per-parcel rows + colour dots).
  const boundarySource = {
    siteBoundaries: reportSite?.siteBoundaries ?? report.siteBoundaries,
    siteBoundary: reportSite?.siteBoundary ?? report.siteBoundary,
    siteArea: reportSite?.siteArea ?? report.siteArea,
    sitePerimeter: reportSite?.sitePerimeter ?? report.sitePerimeter,
    siteCentroid: reportSite?.siteCentroid ?? report.siteCentroid,
  };
  const parcels = React.useMemo(
    () => materializeSiteBoundaries(boundarySource),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      boundarySource.siteBoundaries,
      boundarySource.siteBoundary,
      boundarySource.siteArea,
      boundarySource.sitePerimeter,
      boundarySource.siteCentroid,
    ],
  );
  const parcelAggregate = React.useMemo(() => {
    if (parcels.length === 0) return null;
    const persisted = reportSite?.siteBoundaryAggregate ?? report.siteBoundaryAggregate;
    return persisted ?? computeAggregate(parcels);
  }, [parcels, reportSite?.siteBoundaryAggregate, report.siteBoundaryAggregate]);
  const hasParcelLegend = parcels.length > 0 && parcelAggregate != null;

  // Phase 4 — guarantee a non-blank figure when no Maps key is present. Without
  // a key the live `<Map>` cannot fetch tiles and would render an empty grey
  // frame; fall back to the pure-SVG boundary diagram (same component the hero
  // cover uses), which renders the parcels/layers from geometry alone.
  //
  // HR-QA fix (2026-06-10) — ALSO force the SVG diagram in EXPORT renders
  // (PDF via the /print token path + DOCX). The live `<Map mapId=…>` is a
  // VECTOR (WebGL) map: in the headless print Chromium tiles never paint, so
  // every exported Site Overview shipped as a blank blue frame with a floating
  // pin + Google attribution (viewer/print parity break — the in-app viewer
  // keeps real tiles). The boundary-only SVG is deterministic on paper and
  // preserves all parcel/layer data; this is the sanctioned fallback per the
  // QA directive ("render the boundary-only fallback instead of a blank tile
  // box"). Gated on viewMode === 'export' ONLY — the in-app viewer ('editor'/
  // 'reader') and the live shared view ('public-share') keep real tiles.
  const audience = useViewMode();
  const mapsKeyPresent = hasGoogleMapsKey();
  // William QA (2026-06-17) — the Site Overview EXHIBIT never mounts the live
  // Google vector `<Map>`: the WebGL map rendered a plain roadmap with the
  // boundary/feature polygons MISSING (its imperative useMap() overlays are
  // fragile + there is no fitBounds, so geometry sat off-frame). Scoped to the
  // overview layout — the standalone/embedded interactive maps keep live tiles.
  // William QA (2026-07-02 "Site layers missing") — inside this branch the
  // overview now renders the Google STATIC map with the boundary parcels +
  // layers BAKED IN via Static-Maps path overlays (server-key proxy, bbox
  // auto-fit, panel-palette colour coding); the deterministic labeled SVG
  // boundary diagram is the honest fallback on any static-map failure.
  const useSvgFallback = !mapsKeyPresent || audience === 'export' || isOverviewLayout;

  // The figure-frame border is suppressed in the full-bleed treatment AND in
  // editorial mode (an open figure inherits the cream page — at most ONE hairline
  // applied via `mapFrameStyle` below, never the app `border-border` box).
  const frameBorder = figure.framed && !editorialActive ? 'border border-border' : '';
  // DEFECT 1c + 7 + N1.5C-flat — strip the ungated grey `bg-surface-2` wash
  // UNCONDITIONALLY (William's call: it is not theme-aware, so it leaked a grey
  // box on the cream page in every mode). In EDITORIAL mode ALSO drop the
  // `rounded-*` corners + the figure box, so the map is an OPEN figure that
  // inherits the cream paper with at most the one `roleColor(theme,'hair')`
  // hairline added via `mapFrameStyle`. Flag OFF keeps `rounded-md`/`rounded-sm`
  // so the non-editorial frame geometry stays byte-stable (only `bg-surface-2`
  // — itself ungated per the directive — is removed).
  const roundedOverview = editorialActive ? '' : ' rounded-md';
  const roundedEmbedded = editorialActive ? '' : ' overflow-hidden rounded-sm';
  const mapFrameClass =
    isOverviewLayout
      // NB the `max-h` cap is load-bearing: in the single-column EXPORT/print
      // layout the map spans the full content width (~680px), where
      // `aspect-[16/10]` alone would balloon to ~425px and (a) bump the
      // break-inside-avoid block off the page it shares with the at-a-glance rail
      // and (b) over-allocate, spilling a trailing blank page. Capping at 260px
      // keeps the whole section on one page; the lg: 2-column map (~23rem wide →
      // ~230px tall) is already under the cap, so unaffected.
      ? `aspect-[16/10] w-full h-full min-h-[150px] lg:min-h-[170px] max-h-[260px] ${figure.framed && !editorialActive ? 'border border-border/60' : ''} relative overflow-hidden${roundedOverview}`
      : resolvedLayout === 'embedded'
        ? `aspect-[5/4] h-[220px] sm:h-[240px] lg:h-[260px] w-full max-w-full ${frameBorder} relative${roundedEmbedded}`
        : `aspect-video min-h-[360px] max-h-[540px] ${frameBorder} relative`;
  // EDITORIAL — at most ONE theme-aware hairline around the open figure (the
  // open-exhibit treatment). Themeless / flag-OFF ⇒ no inline border (byte-stable).
  const mapFrameStyle: React.CSSProperties | undefined =
    editorialActive && theme ? { border: `1px solid ${roleColor(theme, 'hair')}` } : undefined;

  // J7 — the side panel is now the boundary-style per-parcel ledger (static /
  // reader / export) or the per-item layer sidebar (interactive editor). Lay
  // out a second column whenever either is present.
  // William QA (2026-06-17) — drop the LAYERS legend (the interactive sidebar AND
  // the per-parcel ledger) from the Site Overview exhibit: the labeled boundary
  // map now carries layer NAMES + area chips on-map, so the side panel is
  // redundant and the exhibit reads as a single clean full-width map. The
  // interactive SiteLayerItemSidebar (checkbox/eye toggles) still renders on the
  // standalone editor map (non-overview), where toggling live layers makes sense.
  const interactiveLayerSidebar =
    !isStatic && audience === 'editor' && reportSiteLayers.length > 0 && !isOverviewLayout;
  const showParcelLegend = hasParcelLegend && !isOverviewLayout;
  const hasSidePanel = interactiveLayerSidebar || showParcelLegend;
  const layersGridClass = hasSidePanel
    ? isOverviewLayout || resolvedLayout === 'embedded'
      ? 'grid grid-cols-1 gap-2 items-start h-full'
      : 'grid grid-cols-1 min-[800px]:grid-cols-[3fr_1fr] gap-2 items-start'
    : isOverviewLayout
      ? 'h-full flex flex-col'
      : '';

  return (
    <div
      className={`space-y-2 ${resolvedLayout === 'embedded' || isOverviewLayout ? 'w-full h-full flex flex-col' : ''}`}
      data-surface-mode={surfaceMode}
    >
      <div className={layersGridClass}>
        <div data-testid="property-map-figure" data-site-map-treatment={figure.treatment}>
          {/* Monograph accent top-rule above the framed figure. Coloured by the
              report THEME accent (roleColor) so it reskins per preset, not the
              fixed app `bg-primary`. Falls back to bg-primary when themeless. */}
          {figure.accentTopRule && (
            <div
              className={`h-[3px] w-12 mb-2 ${theme ? '' : 'bg-primary'}`}
              style={theme ? { backgroundColor: roleColor(theme, 'accent') } : undefined}
              data-testid="property-map-accent-rule"
              aria-hidden
            />
          )}
          {/* Institutional EXHIBIT header bar above the framed figure. The bar
              fill tracks the report THEME accent (roleColor) instead of the app
              `bg-primary`, so it matches the active preset's heading/rule colour
              across all themes; white text reads on the dark institutional
              accent. Themeless ⇒ legacy bg-primary/text-white (byte-stable). */}
          {figure.exhibitHeader && (
            <div
              className={`px-3 py-1.5 mb-2 type-meta font-bold uppercase tracking-widest ${theme ? 'text-white' : 'bg-primary text-white'}`}
              style={theme ? { backgroundColor: roleColor(theme, 'accent') } : undefined}
              data-testid="property-map-exhibit-header"
            >
              {figure.exhibitHeader}
            </div>
          )}
          <div
            className={mapFrameClass}
            style={mapFrameStyle}
            data-map="site"
            data-zoom="14"
            data-maptype={defaultMapTypeId}
            data-surface-mode={surfaceMode}
          >
            {useSvgFallback ? (
              // Pure-SVG fallback — never a blank frame when no Maps key exists.
              // Renders parcels + layer geometry from data alone. PRESERVES all
              // layer/boundary DATA (presentation-only fallback).
              //
              // J7 — boundary-style callout labelling. We now show the
              // leader-line NAME labels (the same treatment the Site Boundary
              // visualization uses) instead of suppressing them — the dedup +
              // priority + cap in HeroCoverBoundaryOverlay keeps them legible.
              // The per-layer detail no longer lives in a LAYERS legend; the
              // labels carry it on the map and the clean per-parcel ledger sits
              // below. EXPORT/PDF gets a Google STATIC-MAP background (boundary
              // + layers baked in via Static-Maps overlays, so Google owns the
              // projection) with this SVG as the honest fallback when the static
              // map is unavailable (no key / fetch error) — never a blank map.
              <div
                className="absolute inset-0 flex items-center justify-center p-2"
                data-testid="property-map-svg-fallback"
              >
                {(() => {
                  // SITE-OVERVIEW CLEAN-MAP (William QA 2026-07-21) — the small
                  // overview figure crowds with leader NAME callouts, per-polygon
                  // "ac" chips, AND the utility legend (a dense KMZ like the
                  // 188-layer Redhead import stacks 'Zone 5 - Sidewalk' ×N,
                  // substation callouts, and setback names into an unreadable
                  // tangle). At this figure size the dedup/priority/cap is not
                  // enough, so suppress EVERY text annotation on the overview map
                  // and let the boundary + colour-coded layer geometry read on
                  // their own (`hideAllLabels`). The larger standalone / export
                  // map keeps its labels (legible at that size) — hence scoping
                  // to `isOverviewLayout` rather than the whole component.
                  const svgOverlay = (
                    <HeroCoverBoundaryOverlay
                      report={report}
                      layers={reportSiteLayers}
                      layerLabels={reportSiteLayers.map((l) => ({ id: l.id, name: l.name?.trim() || l.id }))}
                      textTone={figure.tone}
                      hideAllLabels={isOverviewLayout}
                      className="w-full h-full"
                    />
                  );
                  // NON-overview EXPORT with a client Maps key → the legacy
                  // Google static background (amber boundary + layers baked in,
                  // ring-extent framing). The OVERVIEW exhibit no longer stops
                  // here — it falls through to the colour-coded boundary static
                  // map below (William QA 2026-07-02, superseding the 2026-06-17
                  // pure-SVG-only overview treatment).
                  if (audience === 'export' && mapsKeyPresent && report.location && !isOverviewLayout) {
                    return (
                      <StaticMapImage
                        center={report.location}
                        boundary={path ?? undefined}
                        extraRings={parcels.slice(1).map((p) => p.polygon)}
                        layers={reportSiteLayers}
                        reportId={report.id}
                        className="absolute inset-0 w-full h-full object-cover"
                        fallback={svgOverlay}
                      />
                    );
                  }
                  // NEVER-BLANK (William QA 2026-06-27 "maps still missing") —
                  // HeroCoverBoundaryOverlay returns null when there is NO parcel
                  // boundary AND no layers (no geometry to draw), which left the
                  // overview frame as a literal EMPTY BOX for any site that has a
                  // location but no drawn boundary. This is invisible to the
                  // synthetic-fixture repro (it always has geometry). When there
                  // is nothing to draw, fall back to the Google static map centred
                  // on the site (real location imagery, pin baked in) so the frame
                  // always carries the location; with no location either, a muted
                  // caption — never a blank frame. The with-geometry path above is
                  // untouched (golden snapshots byte-stable).
                  const hasDrawableGeometry = parcels.length > 0 || reportSiteLayers.length > 0;
                  if (!hasDrawableGeometry) {
                    const placeholder = (
                      <div
                        className="absolute inset-0 flex items-center justify-center text-center px-3"
                        data-testid="property-map-empty-placeholder"
                      >
                        <span className={`type-meta ${figure.tone === 'on-dark' ? 'text-white/75' : 'text-text-secondary'}`}>
                          {report.location
                            ? 'Site boundary not drawn – location shown'
                            : 'Site location not specified'}
                        </span>
                      </div>
                    );
                    // Location fallback via the `/api/static-map` SERVER proxy —
                    // the Maps key is injected SERVER-SIDE (never in a client URL),
                    // so this must NOT be gated on the CLIENT `mapsKeyPresent`
                    // (VITE_GOOGLE_MAPS_API_KEY). The old `mapsKeyPresent &&` gate
                    // meant any build without the client key baked in (the
                    // documented server-side-proxy posture) fell straight to the
                    // bare "boundary not drawn" box even though the proxy could
                    // render the location. StaticMapImage degrades to `placeholder`
                    // on any failure (503 unbound server key, fetch/decode error),
                    // so the truly-unavailable case still shows a graceful caption.
                    if (report.location) {
                      return (
                        <StaticMapImage
                          center={report.location}
                          reportId={report.id}
                          maptype={defaultMapTypeId}
                          className="absolute inset-0 w-full h-full object-cover"
                          fallback={placeholder}
                        />
                      );
                    }
                    return placeholder;
                  }
                  // SITE-LAYERS ON THE REPORT MAP (William QA 2026-07-02 "Site
                  // layers missing") — a geometry-bearing site renders the REAL
                  // Google basemap with the boundary parcels + KMZ layers BAKED
                  // IN via Static-Maps `path=`/`markers=` overlays (encoded
                  // polylines through the /api/static-map server proxy; key
                  // stays server-side). Rings are colour-coded with the SAME
                  // per-parcel palette the site panel + parcel legend use, the
                  // frame auto-fits the aggregate boundary bbox, and the centre
                  // pin is dropped (pin-only stays reserved for the no-boundary
                  // branch above, byte-identical). Supersedes the 2026-06-17
                  // pure-SVG-only overview treatment; the labeled SVG diagram
                  // remains the honest fallback on any failure (no server key,
                  // fetch/decode error) — never a blank frame.
                  const staticCenter =
                    (report.location && isFiniteLatLng(report.location) ? report.location : undefined)
                    ?? parcelAggregate?.aggregateCentroid
                    ?? parcels[0]?.centroid;
                  if (staticCenter) {
                    return (
                      <StaticMapImage
                        center={staticCenter}
                        boundary={parcels[0]?.polygon}
                        extraRings={parcels.slice(1).map((p) => p.polygon)}
                        ringColors={PARCEL_RING_COLORS}
                        bbox={parcelAggregate?.bbox}
                        layers={reportSiteLayers}
                        reportId={report.id}
                        maptype={defaultMapTypeId}
                        className="absolute inset-0 w-full h-full object-cover"
                        fallback={svgOverlay}
                      />
                    );
                  }
                  return svgOverlay;
                })()}
              </div>
            ) : (
              <Map
                mapId="bf51a910020fa25a"
                defaultCenter={report.location || { lat: 0, lng: 0 }}
                defaultZoom={14}
                gestureHandling={'none'}
                disableDefaultUI={true}
                keyboardShortcuts={!isStatic && audience === 'editor'}
                fullscreenControl={!isStatic && audience === 'editor'}
                mapTypeControl={!isStatic && audience === 'editor' && !isOverviewLayout}
                zoomControl={!isStatic && audience === 'editor'}
                streetViewControl={!isStatic && audience === 'editor'}
                mapTypeId={defaultMapTypeId}
              >
                <AdvancedMarker position={report.location || { lat: 0, lng: 0 }}>
                  <Pin background={'#D97706'} glyphColor={'#fff'} borderColor={'#92400e'} />
                </AdvancedMarker>
                {path && path.length >= 3 ? <BoundaryPolygon path={path} /> : null}
                {reportSiteLayers.length > 0 && (
                  <SiteLayerOverlays layers={reportSiteLayers} hiddenLayerIds={hiddenLayerIds} />
                )}
              </Map>
            )}
          </div>
        </div>
        {/* J7 — the LAYERS legend (SiteLayerSidebar: PIPELINES / ELECTRICAL /
            CONSTRAINTS / … category counts) is DROPPED on the report overview
            map. The per-layer detail now reads off the on-map boundary-style
            leader labels; below the map we show the clean boundary-style
            per-parcel ledger (TOTAL / dedup / per-parcel rows + colour dots),
            the SAME legend the Site Boundary visualization uses.

            Interactive editor keeps the per-item sidebar so the author can
            still toggle individual layers on the live map. */}
        {interactiveLayerSidebar ? (
          <SiteLayerItemSidebar
            layers={reportSiteLayers}
            hiddenLayerIds={hiddenLayerIds}
            onToggleLayer={toggleLayer}
            surfaceMode={surfaceMode}
            tone={figure.tone}
          />
        ) : showParcelLegend ? (
          <SiteBoundaryParcelLegend
            parcels={parcels}
            aggregate={parcelAggregate}
            singleParcelAcres={boundarySource.siteArea}
            compact
            className="self-start"
          />
        ) : null}
      </div>
      {editorialActive && theme ? (
        // N1.5C-flat — the open figure gets a numbered editorial caption (italic
        // serif, muted, with the bold "Figure N." label) matching every other
        // editorial exhibit caption, instead of the legacy boxed MapCaption.
        <EditorialFigureCaption
          as="figcaption"
          theme={theme}
          number={resolvedFigureNumber}
        >
          {(() => {
            const body = primaryParts.length > 0 ? `Property – ${primaryParts.join(' · ')}` : 'Property';
            return secondaryParts.length > 0 ? `${body} · ${secondaryParts.join(' · ')}` : body;
          })()}
        </EditorialFigureCaption>
      ) : (
        <MapCaption
          testId="property-map-caption"
          primary={(() => {
            const body = primaryParts.length > 0 ? `Property – ${primaryParts.join(' · ')}` : 'Property';
            // Numbered figure prefix ("Figure N." / "Fig. N ·") only when the
            // active treatment numbers figures; legacy reports get no prefix.
            return figure.captionPrefix ? `${figure.captionPrefix}${body}` : body;
          })()}
          secondary={secondaryParts.length > 0 ? secondaryParts.join(' · ') : undefined}
          surfaceMode={surfaceMode}
        />
      )}
    </div>
  );
};
